import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bell, CheckCircle2, Loader2, Lock, Smartphone, Zap, ShieldCheck, Workflow,
} from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';

const API    = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fade   = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

const FEATURES = [
  { Icon: Workflow,   title: 'Controlar agentes desde el móvil',      desc: 'Ver el estado de cada agente, pausarlo, reiniciarlo o cambiar su modo de operación desde cualquier lugar.' },
  { Icon: CheckCircle2, title: 'Aprobar acciones en tiempo real',    desc: 'El human-in-the-loop funciona en el móvil. Recibes la solicitud de aprobación, ves el contexto y apruebas o rechazas con un tap.' },
  { Icon: Bell,       title: 'Alertas de seguridad instantáneas',    desc: 'Si un agente detecta una anomalía, intento de acceso no autorizado o fallo de autenticación, recibes la alerta antes de que escale.' },
  { Icon: Zap,        title: 'Activar flujos desde el móvil',        desc: 'Disparar un flujo de ventas, enviar un recordatorio de cobro o programar una cita sin abrir el escritorio.' },
  { Icon: ShieldCheck,title: 'Auditoría de acciones del agente',     desc: 'Ver el historial completo de decisiones de cada agente con timestamp, contexto y resultado, desde la pantalla del teléfono.' },
  { Icon: Lock,       title: 'Gestionar Auth Links y permisos',      desc: 'Revocar el acceso de un agente a una herramienta en segundos, sin necesitar el escritorio ni contactar soporte.' },
];

const ROLES = [
  { id: 'direccion',  label: 'Dirección',  desc: 'Panel ejecutivo con KPIs en tiempo real y aprobación de decisiones críticas.' },
  { id: 'operaciones', label: 'Operaciones', desc: 'Estado del pipeline, gestión de incidencias y activación de flujos operativos.' },
  { id: 'partner',   label: 'Partner',    desc: 'Vista de todos los clientes gestionados con alertas por workspace.' },
  { id: 'otro',      label: 'Otro',       desc: 'Cuéntanos tu caso al registrarte.' },
];

export default function AppMovilPage() {
  const [form, setForm]       = useState({ email: '', tipo: 'direccion', nombre: '' });
  const [status, setStatus]   = useState('idle'); // idle | loading | success | error
  const [errorMsg, setError]  = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Introduce un email válido.'); return;
    }
    setStatus('loading'); setError('');
    try {
      const res = await fetch(`${API}/waitlist/app-movil`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim(), tipo: form.tipo, nombre: form.nombre.trim() }),
      });
      const d = await res.json();
      if (d.success) setStatus('success');
      else { setStatus('error'); setError('Error al procesar. Inténtalo de nuevo.'); }
    } catch { setStatus('error'); setError('Error de conexión. Inténtalo de nuevo.'); }
  };

  return (
    <>
      <SEO
        title="App Móvil Aynara — Próximamente | Neuralia 369"
        description="Controla agentes, aprueba acciones, recibe alertas de seguridad y activa flujos desde el móvil. En diseño. Únete a la lista de espera para acceso anticipado."
        path="/app-movil"
      />

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-16 bg-[#02060f] border-b border-white/6 overflow-hidden" data-testid="app-movil-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[500px] bg-amber-400/[0.04] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.div variants={fade} className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest border border-amber-400/30 bg-amber-400/10 text-amber-300 rounded-full px-4 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              En diseño · Próximamente
            </motion.div>
            <motion.h1 variants={fade} className="text-white font-heading text-4xl sm:text-5xl font-black leading-tight mb-5" data-testid="app-movil-title">
              App Móvil Aynara.{' '}
              <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
                Tu operación en el bolsillo.
              </span>
            </motion.h1>
            <motion.p variants={fade} className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed mb-5">
              Controla agentes, aprueba acciones críticas, recibe alertas de seguridad y activa
              flujos operativos desde el móvil. Diseñada para dirección y operaciones.
            </motion.p>
            <motion.p variants={fade} className="text-slate-400 text-sm">
              Estado actual: <span className="text-amber-300 font-semibold">En diseño</span>  ·  
              Únete a la lista de espera para acceso anticipado.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── QUÉ HARÁ ── */}
      <section className="py-16 bg-[#030a17] border-b border-white/6" data-testid="app-movil-features">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-10 text-center">
            <p className="editorial-kicker mb-3 text-slate-400">Qué hará la app</p>
            <h2 className="text-white font-heading text-3xl font-black">
              Tu operación, sin abrir el escritorio
            </h2>
          </motion.div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(({ Icon, title, desc }) => (
              <motion.div key={title} variants={fade} className="rounded-2xl border border-white/8 bg-white/[0.02] p-5" data-testid={`feature-card-${title.slice(0,10).replace(/\s/g,'-').toLowerCase()}`}>
                <div className="w-9 h-9 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center mb-4">
                  <Icon className="w-4 h-4 text-amber-300" />
                </div>
                <p className="text-white text-sm font-bold mb-2">{title}</p>
                <p className="text-slate-400 text-xs leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PARA QUIÉN ── */}
      <section className="py-14 bg-[#02060f] border-b border-white/6" data-testid="app-movil-audience">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-8 text-center">
            <p className="editorial-kicker mb-3 text-slate-400">Para quién</p>
            <h2 className="text-white font-heading text-3xl font-black">
              Diseñada para quien decide
            </h2>
          </motion.div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Dirección',    desc: 'KPIs en tiempo real y aprobación de decisiones críticas sin abrir el escritorio.', color: 'text-cyan-300 border-cyan-400/25 bg-cyan-400/6' },
              { label: 'Operaciones', desc: 'Gestión de flujos, incidencias y estado del pipeline desde cualquier lugar.', color: 'text-emerald-300 border-emerald-400/25 bg-emerald-400/6' },
              { label: 'Ventas',      desc: 'Pipeline de leads, aprobación de propuestas y seguimiento de cobros en el móvil.', color: 'text-violet-300 border-violet-400/25 bg-violet-400/6' },
              { label: 'Partners',    desc: 'Vista de todos los clientes gestionados con alertas por workspace y cliente.', color: 'text-amber-300 border-amber-400/25 bg-amber-400/6' },
            ].map(({ label, desc, color }) => (
              <motion.div key={label} variants={fade} className={`rounded-2xl border p-5 ${color.split(' ').slice(1).join(' ')}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${color.split(' ')[0]}`}>{label}</p>
                <p className="text-slate-300 text-xs leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FORMULARIO WAITLIST ── */}
      <section className="py-16 bg-[#030a17]" data-testid="app-movil-waitlist">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <motion.div variants={fade} className="text-center mb-8">
              <div className="w-12 h-12 rounded-2xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center mx-auto mb-5">
                <Smartphone className="w-6 h-6 text-amber-300" />
              </div>
              <p className="editorial-kicker mb-3 text-amber-300/80">Lista de espera</p>
              <h2 className="text-white font-heading text-2xl font-black mb-3">
                Únete para acceso anticipado
              </h2>
              <p className="text-slate-400 text-sm">
                Serás de los primeros en probar la app cuando esté disponible.
                Sin spam. Puedes darte de baja cuando quieras.
              </p>
            </motion.div>

            {status !== 'success' ? (
              <motion.form variants={fade} onSubmit={handleSubmit} className="space-y-4" data-testid="app-movil-form">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Nombre (opcional)</label>
                  <input type="text" value={form.nombre}
                    onChange={e => setForm(f => ({...f, nombre: e.target.value}))}
                    placeholder="Tu nombre"
                    className="w-full bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/50 transition-colors"
                    data-testid="waitlist-nombre-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Email <span className="text-red-400">*</span></label>
                  <input type="email" value={form.email} required
                    onChange={e => { setForm(f => ({...f, email: e.target.value})); setError(''); }}
                    placeholder="tu@empresa.com"
                    className="w-full bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/50 transition-colors"
                    data-testid="waitlist-email-input" />
                  {errorMsg && <p className="text-red-400 text-xs mt-1">{errorMsg}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Perfil de uso</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ROLES.map(r => (
                      <button key={r.id} type="button"
                        onClick={() => setForm(f => ({...f, tipo: r.id}))}
                        className={`text-left rounded-xl border px-3 py-2.5 text-xs font-semibold transition-all ${
                          form.tipo === r.id
                            ? 'border-amber-400/50 bg-amber-400/15 text-amber-300'
                            : 'border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/20'
                        }`}
                        data-testid={`waitlist-tipo-${r.id}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button type="submit" disabled={status === 'loading'}
                  className="w-full btn-primary justify-center"
                  style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
                  data-testid="waitlist-submit-btn">
                  {status === 'loading'
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Registrando…</>
                    : <><Smartphone className="w-4 h-4" /> Unirme a la lista de espera</>
                  }
                </button>
                <p className="text-[11px] text-slate-500 text-center">
                  Sin spam · Sin compromisos · Puedes darte de baja en cualquier momento
                </p>
              </motion.form>
            ) : (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="text-center py-6 rounded-2xl border border-emerald-400/25 bg-emerald-400/8"
                data-testid="waitlist-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-white font-black text-xl mb-2">¡Estás en la lista!</h3>
                <p className="text-slate-300 text-sm leading-relaxed max-w-xs mx-auto">
                  Te avisaremos antes del lanzamiento. Acceso anticipado para los primeros registrados.
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
      <RelatedPages pages={['developers','sistema-multiagente','tendencias','soluciones']} />
    </>
  );
}
